// VideoCommentsSection.tsx — owns the video comment thread state, renders
// heading + composer + tree. Natural page-flow scroll (no inner fixed-height
// scrollbox) — the thread simply grows downward with the page.
import { Fragment } from 'react';
import styled from 'styled-components';
import { useVideoCommentThread } from './useVideoCommentThread';
import { VideoCommentComposer } from './VideoCommentComposer';
import { VideoReplyComposer } from './VideoReplyComposer';
import { VideoCommentCard } from './VideoCommentCard';
import type { VideoComment } from '@/core/models/videoComment';

export interface VideoCommentsSectionProps {
  videoId: string;
}

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-top: 24px;
`;

const Heading = styled.h2`
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: 700;
  color: ${({ theme }) => theme.color.textPrimary};
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => (theme.cardsGlass ? 'transparent' : theme.color.surface)};
  border-radius: ${({ theme }) => theme.radius.md};
`;

const ThreadWrap = styled.div`
  display: flex;
  flex-direction: column;
`;

const RepliesSection = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 54px;
`;

const ToggleBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 4px 0 4px 54px;
  padding: 4px 8px;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.color.accentCyan};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  cursor: pointer;
  border-radius: ${({ theme }) => theme.radius.sm};
  transition: color 0.15s, background 0.15s;

  &:hover {
    background: ${({ theme }) => theme.color.glassBorder};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

const EmptyState = styled.p`
  text-align: center;
  color: ${({ theme }) => theme.color.textMuted};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  padding: 24px 16px;
  margin: 0;
  background: ${({ theme }) => (theme.cardsGlass ? 'transparent' : theme.color.surface)};
  border-radius: ${({ theme }) => theme.radius.md};
`;

export function VideoCommentsSection({ videoId }: VideoCommentsSectionProps) {
  const thread = useVideoCommentThread(videoId);

  // `nested`: whether this card renders inside RepliesSection (already
  // indented 54px) — replying to a top-level comment vs. a nested one needs
  // a different composer indent so the two margins don't stack (108px).
  function cardWithReply(comment: VideoComment, nested: boolean) {
    return (
      <Fragment key={comment.id}>
        <VideoCommentCard comment={comment} onReply={thread.openReply} onToggleLike={thread.toggleLike} />
        {thread.openReplyId === comment.id && (
          <VideoReplyComposer
            replyingToHandle={thread.replyTargetHandle}
            onSubmit={thread.submitReply}
            onCancel={thread.cancelReply}
            onDraftChange={thread.setReplyDraft}
            nested={nested}
          />
        )}
      </Fragment>
    );
  }

  return (
    <Section aria-label="Comments">
      <Heading>Comments ({thread.liveCount})</Heading>

      <VideoCommentComposer onSubmit={thread.addTopLevel} />

      {thread.topLevel.length === 0 ? (
        <EmptyState>Be the first to comment.</EmptyState>
      ) : (
        <List>
          {thread.topLevel.map((top) => {
            const replies = thread.repliesFor(top.id);
            const expanded = thread.isExpanded(top.id);

            return (
              <ThreadWrap key={top.id}>
                {cardWithReply(top, false)}

                {replies.length > 0 && (
                  <ToggleBtn type="button" onClick={() => thread.toggleExpanded(top.id)}>
                    {expanded ? 'Hide replies' : `View replies (${replies.length})`}
                  </ToggleBtn>
                )}

                {expanded && replies.length > 0 && (
                  <RepliesSection>{replies.map((reply) => cardWithReply(reply, true))}</RepliesSection>
                )}
              </ThreadWrap>
            );
          })}
        </List>
      )}
    </Section>
  );
}
